/**
 * The canvas still: drawn in place of the WebGL globe on the poster path,
 * when a script runs but WebGL is unavailable. Draws a dark disc, city
 * lights, the crescent and the sun's glare in the hero framing.
 */
import { HOME_LIGHT, drawSun } from './backlight'
import { FAR_D, HERO_X, farRs, mobileRs } from './camera'
import { cities } from './data/cities'
import { BACK_A } from './globe'
import { LAM_END, TILT } from './globe-look'
import { projector } from './projection'
import type { Canvas2D, View } from './renderer/renderer'
import { MOBILE_WIDTH, measureViewport, type Viewport } from './viewport'

const RAD = Math.PI / 180
const NIGHT = '#08090b'
const MAX_DPR = 3

/** A viewport at device-pixel-ratio 1: the still paints in CSS pixels and lets the context scale. */
function stillViewport(width: number, height: number): Viewport {
  return {
    width,
    height,
    cx: width / 2,
    cy: height / 2,
    min: Math.min(width, height),
    dpr: 1,
    mobile: width < MOBILE_WIDTH,
  }
}

/** The hero-framed View for a 2D canvas of the given size. Mirrors the camera's `heroView`. */
export function heroStillView(width: number, height: number): View {
  const vp = stillViewport(width, height)
  return {
    cx: vp.mobile ? vp.cx : width * HERO_X,
    cy: vp.mobile ? height * 0.34 : vp.cy,
    Rs: vp.mobile ? mobileRs(vp) : farRs(vp),
    d: FAR_D,
    lam: LAM_END * RAD,
    tilt: TILT,
  }
}

function paint(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const vp = stillViewport(width, height)
  const v = heroStillView(width, height)
  const { cx, cy, Rs } = v

  ctx.fillStyle = NIGHT
  ctx.fillRect(0, 0, width, height)

  ctx.fillStyle = '#0d0f13'
  ctx.beginPath()
  ctx.arc(cx, cy, Rs, 0, Math.PI * 2)
  ctx.fill()

  const proj = projector(v)
  ctx.save()
  ctx.beginPath()
  ctx.arc(cx, cy, Rs, 0, Math.PI * 2)
  ctx.clip()
  for (let i = 0; i < cities.count; i++) {
    const pt = proj.project(cities.x[i], cities.y[i], cities.z[i])
    const logPop = Math.log10(cities.population[i])
    const r = Math.max(0.6, (logPop - 4) * 0.55)
    const alpha = Math.min(0.85, 0.12 + (logPop - 4) * 0.16) * (pt.vis ? 1 : BACK_A)
    ctx.globalAlpha = alpha
    ctx.fillStyle = '#ffe8c0'
    ctx.beginPath()
    ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.globalAlpha = 1

  const { lx, ly } = HOME_LIGHT
  const ex = cx + lx * Rs
  const ey = cy + ly * Rs
  const crescR = Rs * 0.09
  const crescGrad = ctx.createRadialGradient(ex, ey, 0, ex, ey, crescR)
  crescGrad.addColorStop(0, 'rgba(190,220,255,0.95)')
  crescGrad.addColorStop(0.45, 'rgba(170,205,255,0.35)')
  crescGrad.addColorStop(1, 'rgba(170,205,255,0)')
  ctx.globalCompositeOperation = 'screen'
  ctx.fillStyle = crescGrad
  ctx.beginPath()
  ctx.arc(ex, ey, crescR, 0, Math.PI * 2)
  ctx.fill()
  ctx.globalCompositeOperation = 'source-over'

  ctx.restore()

  drawSun(ctx as unknown as Canvas2D, vp, HOME_LIGHT, cx, cy, Rs, 1, 0)
}

/**
 * Inject a 2D canvas still into the page on the poster path and fade the preloader.
 * Returns a repaint callback for resize. The still never animates, so it is safe under
 * reduced motion; a resize is a reframe, not movement, so it repaints there regardless.
 */
export function showStill(preloader: HTMLElement | null): () => void {
  const canvas = document.createElement('canvas')
  canvas.className = 'still'
  canvas.id = 'still'
  canvas.setAttribute('aria-hidden', 'true')
  document.body.insertBefore(canvas, document.body.firstChild)

  function repaint(): void {
    const vp = measureViewport(MAX_DPR)
    canvas.width = Math.round(vp.width * vp.dpr)
    canvas.height = Math.round(vp.height * vp.dpr)
    canvas.style.width = vp.width + 'px'
    canvas.style.height = vp.height + 'px'
    const ctx = canvas.getContext('2d')!
    ctx.scale(vp.dpr, vp.dpr)
    paint(ctx, vp.width, vp.height)
  }

  repaint()

  if (preloader) {
    requestAnimationFrame(() => {
      preloader.classList.add('removing')
      preloader.addEventListener('transitionend', () => preloader.remove(), { once: true })
      setTimeout(() => preloader.remove(), 600)
    })
  }

  return repaint
}
